// ---------------------------------------------------------------------------
// A floor under how briefly something may be on screen.
//
// The reported case is the room page's seat notice. It is a real screen with a
// real wait behind it — a shared link, a reload mid-game — and it is also the
// thing that used to appear for two frames on the way into a room the player
// had just made, which reads as a glitch rather than as a wait. Making the fast
// case not draw it at all is the fix; this is the other half, for the case that
// draws it and then gets its answer a moment later.
//
// **It is a floor, never a delay.** Nothing here holds anything back that was
// not already up: `showing` false at every render costs exactly nothing, so a
// room that arrives before the notice is drawn is not slowed down by a
// millisecond. That asymmetry is the whole point — a minimum that also applied
// to the case it never fired in would make the ordinary path worse to fix the
// rare one.
//
// The same three rules `useHeldTrue` is written to, and for the same reasons:
// no clock is read, no elapsed time is measured, and the state changes land in
// the render that notices rather than in an effect. The timer already knows
// when the floor is served, so nothing has to count.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';

/**
 * How long the room page's seat notice stays up once it has gone up.
 *
 * Long enough to be read as a sentence rather than seen as a flicker, short
 * enough that somebody whose room was there all along does not feel held. It is
 * a readability floor rather than an animation, so reduced motion does not
 * shorten it — there is nothing moving to reduce.
 */
export const SEAT_NOTICE_MIN_MS = 600;

/**
 * Whether something must be on screen: because it is wanted, or because it was
 * wanted so recently that taking it away now would be a flash.
 *
 * `showing` is what the caller would draw with no floor at all. What comes back
 * is what it should draw.
 */
export function useMinimumOnScreen(showing: boolean, ms: number): boolean {
  // `holding` from the moment it goes up, `done` once the floor is served, and
  // back to `idle` when it finally comes down — so a second appearance gets a
  // floor of its own rather than inheriting the first one's expiry.
  const [phase, setPhase] = useState<'idle' | 'holding' | 'done'>(showing ? 'holding' : 'idle');

  // React's own "adjusting state when a prop changes" pattern, both ways round.
  // Rendered is committed: there is no frame in which this is on screen and the
  // floor is not yet in force.
  if (showing && phase === 'idle') setPhase('holding');
  if (!showing && phase === 'done') setPhase('idle');

  useEffect(() => {
    if (phase !== 'holding') return;
    // Deliberately not keyed on `showing`. The floor outlives the reason for
    // it — that is the whole job — so a timer torn down when the caller stopped
    // wanting the screen would end the hold at exactly the moment it starts to
    // matter.
    const timer = window.setTimeout(() => setPhase('done'), ms);
    return () => window.clearTimeout(timer);
  }, [phase, ms]);

  return showing || phase === 'holding';
}
